import React from 'react';
import classNames from 'classnames';
import { Button, Icon, IconNames, ToggleGroup } from '@dotcom/crown-ui';
import styles from './editor-header.module.scss';
import { EditorHeaderProps } from './editor-header.types';
import { SaveIcon } from './save-icon';
import { Quantity } from '../quantity/quantity';

export const EditorHeader: React.FC<EditorHeaderProps> = ({
  onClose,
  onSave,
  isSaving = false,
  activeView,
  onViewChange,
  onAddressClick,
  isAddressDisabled,
  isAddressRestoring,
  isSaveDisabled,
  showAddressButton = true,
  showViewToggle: showViewToggleProp,
  centerSlot,
  rightSlot,
  variant = 'editor',
  previewActions,
  testId,
  className,
}) => {
  const toggleOptions = [
    { value: 'edit', icon: IconNames.Edit },
    { value: 'preview', icon: IconNames.PasswordShowBold },
  ];
  const showViewToggle =
    showViewToggleProp !== undefined ? showViewToggleProp : variant === 'editor' && !!activeView && !!onViewChange;

  return (
    <header className={classNames(styles['editor-header'], className)} data-testid={testId}>
      <div className={styles['editor-header-left']}>
        {onClose && (
          <Button
            type="button"
            size="icon"
            variant="link"
            className={classNames(
              styles['editor-header-circle-btn'],
              isSaving && styles['editor-header-circle-btn--saving'],
            )}
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close"
            testId={testId ? `${testId}-close` : undefined}
          >
            <span className={styles['editor-header-icon']}>
              <Icon name={IconNames.MenuCloseBold} size="16" color="purple" />
            </span>
          </Button>
        )}
        {onSave && (
          <Button
            type="button"
            size="icon"
            variant="link"
            className={classNames(
              styles['editor-header-circle-btn'],
              isSaving && styles['editor-header-circle-btn--saving'],
            )}
            onClick={onSave}
            disabled={isSaving || isSaveDisabled}
            aria-label={isSaving ? 'Saving' : 'Save'}
            testId={testId ? `${testId}-save` : undefined}
          >
            <span className={styles['editor-header-icon']}>
              {isSaving ? (
                <Icon name={IconNames.SyncSyncInactive} size="16" className={styles['editor-header-spin']} />
              ) : (
                <SaveIcon size={20} />
              )}
            </span>
          </Button>
        )}
      </div>

      <div
        className={classNames(
          styles['editor-header-center'],
          !showViewToggle && styles['editor-header-center--viewport-centered'],
        )}
      >
        {showViewToggle && activeView && onViewChange ? (
          <ToggleGroup
            options={toggleOptions}
            activeValue={activeView}
            onChange={(value) => onViewChange(value as 'edit' | 'preview')}
            testId={testId ? `${testId}-toggle` : undefined}
            className={styles['editor-header-view-toggle']}
          />
        ) : (
          centerSlot
        )}
      </div>

      <div
        className={`${styles['editor-header-right']} ${
          variant === 'envelope' ? styles['editor-header-right-quantity'] : ''
        }`}
      >
        {variant === 'preview' && previewActions ? (
          <>
            <Button
              type="button"
              variant="secondary"
              size="medium"
              className={styles['editor-header-edit-btn']}
              onClick={previewActions.onEditClick}
              testId={testId ? `${testId}-edit` : undefined}
            >
              {'< Edit'}
            </Button>
            {previewActions.ctaLabel && previewActions.onCtaClick && (
              <Button
                type="button"
                variant="primary"
                size="medium"
                className={styles['editor-header-preview-cta']}
                onClick={previewActions.onCtaClick}
                disabled={previewActions.isCtaDisabled}
                testId={testId ? `${testId}-preview-cta` : undefined}
              >
                {previewActions.ctaLabel}
              </Button>
            )}
          </>
        ) : (
          <>
            {showAddressButton && onAddressClick && (
              <Button
                variant="primary"
                size="medium"
                className={classNames(
                  styles['editor-header-address-btn'],
                  isAddressRestoring && styles['editor-header-address-btn--restoring'],
                )}
                onClick={onAddressClick}
                disabled={isAddressDisabled || isSaving}
                testId={testId ? `${testId}-address` : undefined}
              >
                Address &gt;
              </Button>
            )}
            {variant === 'envelope' && !rightSlot ? <Quantity /> : rightSlot}
            {!showAddressButton && !rightSlot && variant !== 'envelope' && (
              <div
                className={styles['editor-header-empty-slot']}
                data-testid={testId ? `${testId}-empty-slot` : undefined}
              />
            )}
          </>
        )}
      </div>
    </header>
  );
};
